import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { BaseDbObject } from "../models/db";
import {
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "./utils";

const ENTITY_KEY = "betterAuth";

// Organization interface
export interface Organization extends BaseDbObject {
  name: string;
  org_type: string;
  description?: string;
}

export interface OrganizationMemberPermission extends BaseDbObject {
  permission_type: string;
  subTypes: string[];
}

export interface OrganizationMemberRelation extends BaseDbObject {
  user_id: string;
  relation_type: "risk" | "freight";
  customer_id: string;
  Customer?: {
    long_name: string;
  };
}

// Organization member interface
export interface OrganizationMember extends BaseDbObject {
  user_id: string; // Better Auth member ID
  organization_id: string;
  role: string;
  permissions: OrganizationMemberPermission[];
  relations?: OrganizationMemberRelation[];
  organization?: {
    id: string;
    name: string;
    org_type: string;
  };
  authuser?: {
    id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    user_id?: string; // Database user_id - ONLY used for display logic (internal user check)
    customer_id?: string;
  };
}

// Create auth user request interface
export interface CreateAuthUserRequest {
  phoneNumber: string;
  name: string;
  organizationId: string;
}

// Create auth user response interface
export interface CreateAuthUserResponse {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  member: {
    id: string;
    role: string;
    organizationId: string;
    userId: string;
  };
}

// Auth user relation change interface
export interface AuthUserRelationChange {
  user_id: string;
  customer_id: string;
  relation_type: "risk";
  should_delete?: boolean;
}

// Update auth user relations request interface
export interface UpdateAuthUserRelationsRequest {
  changes: AuthUserRelationChange[];
}

// Update auth user relations response interface
export interface UpdateAuthUserRelationsResponse {
  deleted_count: number;
  created_count: number;
  deleted_ids: string[];
  created_ids: string[];
}

// Validation schemas
export const ListOrganizationsValidation = z.object({
  query: z.object({
    org_type: z.string().optional()
  }).optional()
});

export const AssignUserValidation = z.object({
  params: z.object({
    organizationId: z.string().uuid()
  }),
  body: z.object({
    authUserId: z.string().uuid(),
    role: z.string().optional().default('member')
  })
});

export const UpdatePermissionsValidation = z.object({
  params: z.object({
    organizationId: z.string().uuid(),
    memberId: z.string().uuid()
  }),
  body: z.object({
    permissionType: z.string(),
    enabled: z.boolean(),
    subTypes: z.array(z.string()).optional()
  })
});

export const CreateAuthUserValidation = z.object({
  body: z.object({
    phoneNumber: z.string().min(1, "Phone number is required"),
    name: z.string().min(1, "Name is required"),
    organizationId: z.string().uuid("Organization ID must be a valid UUID")
  })
});

export const UpdateAuthUserValidation = z.object({
  params: z.object({
    id: z.string().uuid("User ID must be a valid UUID")
  }),
  body: z.object({
    name: z.string().min(1, "Name is required").optional().nullable(),
  })
});

export const UpdateAuthUserRelationsValidation = z.object({
  body: z.object({
    changes: z.array(
      z.object({
        user_id: z.string().uuid("User ID must be a valid UUID"),
        customer_id: z.string().uuid("Customer ID must be a valid UUID"),
        relation_type: z.literal("risk"),
        should_delete: z.boolean().optional()
      })
    ).min(1, "At least one change is required")
  })
});

// Hook to list organizations
export function useListOrganizations(orgType?: string) {
  const queryParams = new URLSearchParams();
  if (orgType) {
    queryParams.append('org_type', orgType);
  }

  return useQuery({
    queryKey: [ENTITY_KEY, "organizations", orgType],
    queryFn: () => fetchApiGet<Organization[]>("auth/manage/organizations", queryParams),
    retry: 1,
  });
}

// Hook to list members of an organization
export function useOrganizationMembers(organizationId?: string) {
  return useQuery({
    queryKey: [ENTITY_KEY, "organizations", organizationId, "members"],
    queryFn: () => fetchApiGet<OrganizationMember[]>(`auth/manage/organizations/${organizationId}/members`),
    retry: 1,
    enabled: !!organizationId,
  });
}

// Hook to assign user to organization
export function useAssignUserToOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      authUserId,
      role = 'member'
    }: {
      organizationId: string;
      authUserId: string;
      role?: string;
    }) => {
      return fetchApiPost(`auth/manage/organizations/${organizationId}/members`, {
        authUserId,
        role
      });
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_KEY, "organizations", organizationId, "members"]
      });
    },
  });
}

// Hook to update user permissions
export function useUpdateUserPermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      memberId,
      permissionType,
      enabled,
      subTypes
    }: {
      organizationId: string;
      memberId: string;
      permissionType: string;
      enabled: boolean;
      subTypes?: string[];
    }) => {
      return fetchApiPut(`auth/manage/organizations/${organizationId}/members/${memberId}/permissions`, {
        permissionType,
        enabled,
        subTypes
      });
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_KEY, "organizations", organizationId, "members"]
      });
    },
  });
}

// Hook to create auth user
export function useCreateAuthUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      phoneNumber,
      name,
      organizationId
    }: CreateAuthUserRequest) => {
      return fetchApiPost<CreateAuthUserResponse>("auth/manage/auth-users", {
        phoneNumber,
        name,
        organizationId
      });
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_KEY, "organizations", organizationId, "members"]
      });
    },
  });
}

export function useUpdateAuthUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: z.infer<typeof UpdateAuthUserValidation>) => {
      return fetchApiPut<CreateAuthUserResponse>(`auth/manage/auth-users/${data.params.id}`, data.body);
    },
    onSuccess: (_, {  }) => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_KEY, "organizations"]
      });
    },
  });
}

// Hook to update auth user relations
export function useUpdateAuthUserRelations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      changes
    }: UpdateAuthUserRelationsRequest) => {
      return fetchApiPut<UpdateAuthUserRelationsResponse>("auth/manage/auth-users/relations", {
        changes
      });
    },
    onSuccess: () => {
      // Invalidate organization members queries to refresh relation data
      queryClient.invalidateQueries({
        queryKey: [ENTITY_KEY, "organizations"]
      });
    },
  });
}
